import { Navigate, Outlet, Route, Routes } from "react-router-dom";
import { AppShell } from "./components/AppShell";
import { useSession } from "./lib/session";
import { HomePage } from "./pages/HomePage";
import { AuthPage } from "./pages/AuthPage";
import { CreatorDashboard } from "./pages/creator/CreatorDashboard";
import { DramaFormPage } from "./pages/creator/DramaFormPage";
import { ContractsPage } from "./pages/creator/ContractsPage";
import { BuyerMarket } from "./pages/buyer/BuyerMarket";
import { AdminDashboard } from "./pages/admin/AdminDashboard";
import { AdminContent } from "./pages/admin/AdminContent";
import { PremierePage } from "./pages/PremierePage";
import { LegalPage } from "./pages/LegalPage";
import { AdminContracts } from "./pages/admin/AdminContracts";
import { AdminLegal } from "./pages/admin/AdminLegal";
import { FinanceCenter } from "./pages/FinanceCenter";
import { AdminDistribution } from "./pages/admin/AdminDistribution";
import { AdminPremieres } from "./pages/admin/AdminPremieres";
import { AdminVerification } from "./pages/admin/AdminVerification";
import { AdminAudit } from "./pages/admin/AdminAudit";
import { CreatorVerification } from "./pages/creator/CreatorVerification";
import { BuyerOpportunities } from "./pages/buyer/BuyerOpportunities";
import { BuyerProfile } from "./pages/buyer/BuyerProfile";
import { HelpCenterPage } from "./pages/HelpCenterPage";

type WorkspaceRole = "creator" | "buyer" | "admin";

function workspacePath(role: WorkspaceRole) {
  return role === "admin" ? "/admin" : role === "buyer" ? "/buyer" : "/creator";
}

function RoleHome() {
  const { user, loading } = useSession();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={workspacePath(user.role)} replace />;
}

function RoleGate({ role }: { role: WorkspaceRole }) {
  const { user, loading } = useSession();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== role) return <Navigate to={workspacePath(user.role)} replace />;
  return <Outlet />;
}

function HelpRoleGate() {
  const { user, loading } = useSession();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;
  return <Outlet />;
}

export function App() {
  return <Routes>
    <Route path="/" element={<HomePage />} />
    <Route path="/premieres" element={<PremierePage />} />
    <Route path="/legal/:slug" element={<LegalPage />} />
    <Route path="/login" element={<AuthPage mode="login" />} />
    <Route path="/register" element={<AuthPage mode="register" />} />
    <Route path="/workspace" element={<RoleHome />} />
    <Route element={<AppShell />}>
      <Route element={<RoleGate role="creator" />}>
        <Route path="/creator" element={<CreatorDashboard />} />
        <Route path="/creator/submit" element={<DramaFormPage />} />
        <Route path="/creator/dramas/:id" element={<DramaFormPage />} />
        <Route path="/creator/contracts" element={<ContractsPage />} />
        <Route path="/creator/finance" element={<FinanceCenter />} />
        <Route path="/creator/verification" element={<CreatorVerification />} />
      </Route>
      <Route element={<RoleGate role="buyer" />}>
        <Route path="/buyer" element={<BuyerOpportunities />} />
        <Route path="/buyer/market" element={<BuyerMarket />} />
        <Route path="/buyer/contracts" element={<ContractsPage />} />
        <Route path="/buyer/finance" element={<FinanceCenter />} />
        <Route path="/buyer/profile" element={<BuyerProfile />} />
      </Route>
      <Route element={<HelpRoleGate />}>
        <Route path="/help" element={<HelpCenterPage />} />
      </Route>
      <Route element={<RoleGate role="admin" />}>
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/distribution" element={<AdminDistribution />} />
        <Route path="/admin/premieres" element={<AdminPremieres />} />
        <Route path="/admin/verification" element={<AdminVerification />} />
        <Route path="/admin/audit" element={<AdminAudit />} />
        <Route path="/admin/content" element={<AdminContent />} />
        <Route path="/admin/contracts" element={<AdminContracts />} />
        <Route path="/admin/finance" element={<FinanceCenter />} />
        <Route path="/admin/legal" element={<AdminLegal />} />
      </Route>
    </Route>
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>;
}
